import React, { useEffect, useMemo, useState } from 'react';

import {
  BankOutlined,
  BellOutlined,
  CheckCircleOutlined,
  CloseCircleOutlined,
  FlagOutlined,
  LoadingOutlined,
  LockOutlined,
  UserOutlined,
} from '@ant-design/icons';
import {
  Alert,
  Avatar,
  Button,
  Col,
  Divider,
  Form,
  Input,
  Modal,
  Row,
  Skeleton,
  Space,
  Spin,
  Tabs,
  Tag,
  Upload,
  message,
} from 'antd';
import type { UploadProps } from 'antd';
import PhoneInput from 'antd-phone-input';
import { useTranslation } from 'react-i18next';
import { useDispatch } from 'react-redux';

import AuthAction from 'redux/actions/authActions/Actions';

import { User } from 'models/User';

import { AuthService } from 'services/AuthService';
import { UserService } from 'services/UserService';

import { useThemeLayoutConfig } from '../../../config/ThemeDashboardLayoutConfig';
import appI18n from '../../../config/i18n';

import './ProfileEditModal.scss';

interface ProfileEditModalProps {
  isOpen: boolean;
  onClose: () => void;
}

function ProfileEditModal({ isOpen, onClose }: ProfileEditModalProps) {
  const { t } = useTranslation(undefined, { i18n: appI18n });
  const dispatch = useDispatch();
  const customConfig = useThemeLayoutConfig();
  const [initialLoading, setInitialLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [user, setUser] = useState<User>();
  const [personalForm] = Form.useForm();
  const [passwordForm] = Form.useForm();
  const [activeKey, setActiveKey] = useState<string>('personal');
  const [avatarFile, setAvatarFile] = useState<File | undefined>(undefined);
  const [avatarRemoved, setAvatarRemoved] = useState<boolean>(false);
  const [avatarPreview, setAvatarPreview] = useState<string | undefined>(
    undefined
  );
  const [avatarLoading, setAvatarLoading] = useState<boolean>(false);
  const [avatarUploading, setAvatarUploading] = useState<boolean>(false);
  const [avatarTempKey, setAvatarTempKey] = useState<string | undefined>(
    undefined
  );
  const [newPwdPreview, setNewPwdPreview] = useState<string>('');
  const [isMobile, setIsMobile] = useState<boolean>(false);
  const [viewportWidth, setViewportWidth] = useState<number>(1024);
  const sectionBusy = saving || avatarUploading;
  const showAvatarSpinner = avatarLoading || avatarUploading;
  const hexToHsl = (hex: string) => {
    const h = hex.replace('#', '');
    const full =
      h.length === 3
        ? h
            .split('')
            .map((c) => c + c)
            .join('')
        : h;
    const r = parseInt(full.substring(0, 2), 16) / 255;
    const g = parseInt(full.substring(2, 4), 16) / 255;
    const b = parseInt(full.substring(4, 6), 16) / 255;
    const max = Math.max(r, g, b);
    const min = Math.min(r, g, b);
    let hVal = 0;
    let s = 0;
    const l = (max + min) / 2;
    if (max !== min) {
      const d = max - min;
      s = l > 0.5 ? d / (2 - max - min) : d / (max + min);
      switch (max) {
        case r:
          hVal = (g - b) / d + (g < b ? 6 : 0);
          break;
        case g:
          hVal = (b - r) / d + 2;
          break;
        default:
          hVal = (r - g) / d + 4;
      }
      hVal /= 6;
    }
    return {
      h: Math.round(hVal * 360),
      s: Math.round(s * 100),
      l: Math.round(l * 100),
    };
  };
  const hslToHex = (h: number, s: number, l: number) => {
    s /= 100;
    l /= 100;
    const c = (1 - Math.abs(2 * l - 1)) * s;
    const x = c * (1 - Math.abs(((h / 60) % 2) - 1));
    const m = l - c / 2;
    let r = 0,
      g = 0,
      b = 0;
    if (0 <= h && h < 60) {
      r = c;
      g = x;
      b = 0;
    } else if (60 <= h && h < 120) {
      r = x;
      g = c;
      b = 0;
    } else if (120 <= h && h < 180) {
      r = 0;
      g = c;
      b = x;
    } else if (180 <= h && h < 240) {
      r = 0;
      g = x;
      b = c;
    } else if (240 <= h && h < 300) {
      r = x;
      g = 0;
      b = c;
    } else {
      r = c;
      g = 0;
      b = x;
    }
    const toHex = (v: number) => {
      const n = Math.round((v + m) * 255);
      const s2 = n.toString(16).padStart(2, '0');
      return s2;
    };
    return `#${toHex(r)}${toHex(g)}${toHex(b)}`;
  };
  const deriveAnalogous = (hex: string, shift: number, lighten: number) => {
    const hsl = hexToHsl(hex);
    const h = (hsl.h + shift) % 360;
    const l = Math.min(100, Math.max(0, hsl.l + lighten));
    return hslToHex(h, hsl.s, l);
  };

  useEffect(() => {
    if (!isOpen) {
      setInitialLoading(false);
      setSaving(false);
      return;
    }

    document.body.style.overflow = 'hidden';
    setActiveKey('personal');
    personalForm.resetFields();
    passwordForm.resetFields();
    fetchData();

    return () => {
      document.body.style.overflow = '';
    };
  }, [isOpen]);

  useEffect(() => {
    const updateViewport = () => {
      const w = window.innerWidth || 1024;
      setViewportWidth(w);
      setIsMobile(w <= 576);
    };
    updateViewport();
    window.addEventListener('resize', updateViewport);
    return () => {
      window.removeEventListener('resize', updateViewport);
    };
  }, []);

  const fetchData = async () => {
    setInitialLoading(true);
    const response = await AuthService.verifySession();
    if (response.success) {
      const userData = response.data as User;
      setUser(userData);

      personalForm.resetFields();
      personalForm.setFieldValue('name', userData.name);
      personalForm.setFieldValue('lastname', userData.lastname);
      personalForm.setFieldValue('email', userData.email);
      personalForm.setFieldValue('company', userData.company?.name || '');
      personalForm.setFieldValue(
        'country',
        userData.company?.country?.name || ''
      );
      personalForm.setFieldValue(
        'roles',
        (userData.roles || []).map((r: any) => r.name)
      );

      const dial = userData.dialCode
        ? parseInt(String(userData.dialCode).replace('+', ''))
        : parseInt(String(userData.company?.country?.dialCode || '1'));
      if (userData.phone && userData.dialCode) {
        personalForm.setFieldValue('phone', {
          ...personalForm.getFieldValue('phone'),
          countryCode: dial,
          areaCode: userData.phone,
          phoneNumber: null,
        });
      } else {
        personalForm.setFieldValue('phone', {
          ...personalForm.getFieldValue('phone'),
          countryCode: dial,
        });
      }

      setAvatarPreview(userData.avatar || undefined);
      setAvatarFile(undefined);
      setAvatarTempKey(undefined);
      setAvatarRemoved(false);
      if (userData.avatar) {
        setAvatarLoading(true);
        const img = new Image();
        img.onload = () => setAvatarLoading(false);
        img.onerror = () => setAvatarLoading(false);
        img.src = userData.avatar;
      }
    }
    setInitialLoading(false);
  };

  const initials = useMemo(() => {
    const n = user?.name || '';
    const l = user?.lastname || '';
    return `${n.slice(0, 1)}${l.slice(0, 1)}`.toUpperCase();
  }, [user]);

  const uploadProps: UploadProps = {
    accept: 'image/png,image/jpeg,image/jpg,image/gif',
    showUploadList: false,
    beforeUpload: (file) => {
      const isValidType = [
        'image/png',
        'image/jpeg',
        'image/jpg',
        'image/gif',
      ].includes(file.type);
      if (!isValidType) {
        message.error(
          t(
            'components.layout.profile_edit_modal.validations.avatar_format_error'
          )
        );
        return Upload.LIST_IGNORE;
      }
      if (file.size > 1.2 * 1024 * 1024) {
        message.error(
          t(
            'components.layout.profile_edit_modal.validations.avatar_size_error'
          )
        );
        return Upload.LIST_IGNORE;
      }
      setAvatarUploading(true);
      message.open({
        type: 'info',
        content: t(
          'components.layout.profile_edit_modal.messages.uploading_image'
        ),
        key: 'avatar-upload',
        duration: 0,
      });
      (async () => {
        const response = await UserService.uploadTempAvatar(file);
        if (response.success) {
          const data = response.data as { key: string; url: string };
          setAvatarTempKey(data.key);
          setAvatarPreview(data.url);
          setAvatarRemoved(false);
        } else {
          const err = response.data as any;
          message.error(
            err?.message ||
              t(
                'components.layout.profile_edit_modal.errors.upload_avatar_failed'
              )
          );
        }
        message.destroy('avatar-upload');
        setAvatarUploading(false);
      })();
      return Upload.LIST_IGNORE;
    },
  };

  const handleRemoveAvatar = () => {
    setAvatarFile(undefined);
    setAvatarTempKey(undefined);
    setAvatarRemoved(true);
    setAvatarPreview(undefined);
  };

  const handleCancel = () => {
    if (saving || initialLoading) return;
    personalForm.resetFields();
    passwordForm.resetFields();
    setNewPwdPreview('');
    setActiveKey('personal');
    setAvatarFile(undefined);
    setAvatarTempKey(undefined);
    setAvatarRemoved(false);
    setAvatarPreview(undefined);
    onClose();
  };

  const handleSavePersonalInfo = async () => {
    try {
      const values = await personalForm.validateFields();
      setSaving(true);
      const dialCode =
        values.phone && values.phone.phoneNumber === null
          ? null
          : `+${values.phone.countryCode}`;
      const phone =
        values.phone && values.phone.phoneNumber === null
          ? null
          : `${values.phone.areaCode || ''}${values.phone.phoneNumber || ''}`;
      const avatarKeyPayload = avatarRemoved
        ? null
        : avatarTempKey
        ? avatarTempKey
        : undefined;
      const response = await UserService.updateCurrentUser({
        name: values.name,
        lastname: values.lastname,
        dialCode,
        phone,
        avatarKey: avatarKeyPayload,
      });
      if (response.success) {
        const responseUserUpdated = await AuthService.verifySession();
        if (responseUserUpdated.success) {
          const userData = responseUserUpdated.data as User;
          dispatch(AuthAction.signIn(userData));
        }

        message.success(
          t(
            'components.layout.profile_edit_modal.messages.personal_info_updated'
          )
        );
      } else {
        const err = response.data as any;
        message.error(
          err?.message ||
            t(
              'components.layout.profile_edit_modal.errors.personal_info_update_failed'
            )
        );
      }
    } catch (e) {
      return;
    } finally {
      setSaving(false);
    }
  };

  const passwordRulesRegex = /^(?=.*[A-Z])(?=.*\d)(?=.*[^\w\s]).{8,}$/;

  const handleSavePassword = async () => {
    try {
      const values = await passwordForm.validateFields();
      setSaving(true);
      const response = await UserService.updatePasswordForCurrentUser({
        currentPassword: values.currentPassword,
        newPassword: values.newPassword,
      });
      if (response.success) {
        message.success(
          t('components.layout.profile_edit_modal.messages.password_updated')
        );
        passwordForm.resetFields();
        setNewPwdPreview('');
      } else {
        const err = response.data as any;
        message.error(
          err?.message ||
            t(
              'components.layout.profile_edit_modal.errors.password_update_failed'
            )
        );
      }
    } catch (e) {
      return;
    } finally {
      setSaving(false);
    }
  };

  return (
    <Modal
      open={isOpen}
      onCancel={handleCancel}
      closable={!saving && !initialLoading}
      maskClosable={false}
      keyboard={!saving && !initialLoading}
      destroyOnClose
      centered
      width={isMobile ? Math.max(320, viewportWidth - 24) : 1000}
      title={t('components.layout.profile_edit_modal.title')}
      styles={{
        body: {
          paddingTop: '10px',
          maxHeight: isMobile ? '70vh' : 'calc(100vh - 240px)',
          minHeight: isMobile ? undefined : 510,
          overflowY: 'auto',
          overflowX: 'hidden',
        },
      }}
      footer={[
        <Button key="cancel" onClick={handleCancel} disabled={saving}>
          {t('components.layout.profile_edit_modal.buttons.cancel')}
        </Button>,
        <Button
          key="save"
          type="primary"
          onClick={() => {
            if (activeKey === 'personal') {
              handleSavePersonalInfo();
            } else if (activeKey === 'password') {
              handleSavePassword();
            }
          }}
          loading={saving}
          disabled={sectionBusy}
        >
          {t('components.layout.profile_edit_modal.buttons.save_changes')}
        </Button>,
      ]}
    >
      {!user || initialLoading ? (
        <Skeleton active paragraph={{ rows: 10 }} />
      ) : (
        <Row gutter={24}>
          <Col xs={24}>
            <Tabs
              activeKey={activeKey}
              onChange={(key) => {
                if (!sectionBusy) {
                  setActiveKey(key as string);
                }
              }}
              tabPosition={isMobile ? 'top' : 'left'}
              items={[
                {
                  key: 'personal',
                  label: (
                    <Space align="center" size={6}>
                      <UserOutlined />
                      <span>
                        {t(
                          'components.layout.profile_edit_modal.tabs.personal_info'
                        )}
                      </span>
                    </Space>
                  ),
                  children: (
                    <Space
                      direction="vertical"
                      style={{ width: '100%' }}
                      size={isMobile ? 12 : 16}
                    >
                      <Space size={8} align="center" style={{ width: '100%' }}>
                        <Spin
                          spinning={showAvatarSpinner}
                          indicator={
                            <LoadingOutlined style={{ fontSize: 20 }} spin />
                          }
                        >
                          {avatarPreview ? (
                            <Avatar
                              src={avatarPreview}
                              size={64}
                              style={{
                                backgroundColor:
                                  customConfig.customValues.secondaryColor ||
                                  '#cfe8fc',
                                color:
                                  customConfig.theme.token?.colorPrimary ||
                                  '#0077cc',
                              }}
                            />
                          ) : (
                            <Avatar
                              size={64}
                              style={{
                                backgroundColor:
                                  customConfig.customValues.secondaryColor ||
                                  '#cfe8fc',
                                color:
                                  customConfig.theme.token?.colorPrimary ||
                                  '#0077cc',
                                textTransform: 'uppercase',
                              }}
                            >
                              {initials}
                            </Avatar>
                          )}
                        </Spin>
                        <Upload {...uploadProps}>
                          <Button type="primary" disabled={sectionBusy}>
                            {t(
                              'components.layout.profile_edit_modal.buttons.upload'
                            )}
                          </Button>
                        </Upload>
                        {(avatarPreview || avatarFile) && (
                          <Button
                            disabled={sectionBusy}
                            onClick={handleRemoveAvatar}
                          >
                            {t(
                              'components.layout.profile_edit_modal.buttons.delete'
                            )}
                          </Button>
                        )}
                      </Space>
                      <div
                        style={{
                          color: customConfig.theme.token?.colorTextSecondary,
                        }}
                      >
                        {t(
                          'components.layout.profile_edit_modal.messages.avatar_requirements'
                        )}
                      </div>
                      <Divider style={{ margin: '8px 0' }} />
                      <Form
                        form={personalForm}
                        layout="vertical"
                        autoComplete="off"
                      >
                        <Row gutter={24}>
                          <Col xs={24} md={12}>
                            <Form.Item
                              name="name"
                              label={t(
                                'components.layout.profile_edit_modal.labels.names'
                              )}
                              rules={[
                                {
                                  required: true,
                                  message: t(
                                    'components.layout.profile_edit_modal.validations.names_required'
                                  ),
                                },
                              ]}
                            >
                              <Input
                                disabled={sectionBusy}
                                placeholder={t(
                                  'components.layout.profile_edit_modal.placeholders.enter_names'
                                )}
                              />
                            </Form.Item>
                          </Col>
                          <Col xs={24} md={12}>
                            <Form.Item
                              name="lastname"
                              label={t(
                                'components.layout.profile_edit_modal.labels.lastnames'
                              )}
                              rules={[
                                {
                                  required: true,
                                  message: t(
                                    'components.layout.profile_edit_modal.validations.lastnames_required'
                                  ),
                                },
                              ]}
                            >
                              <Input
                                disabled={sectionBusy}
                                placeholder={t(
                                  'components.layout.profile_edit_modal.placeholders.enter_lastnames'
                                )}
                              />
                            </Form.Item>
                          </Col>
                        </Row>
                        <Row gutter={24}>
                          <Col xs={24} md={12}>
                            <Form.Item
                              name="email"
                              label={t(
                                'components.layout.profile_edit_modal.labels.email'
                              )}
                            >
                              <Input disabled addonBefore={'@'} />
                            </Form.Item>
                          </Col>
                          <Col xs={24} md={12}>
                            <Form.Item
                              name="phone"
                              label={t(
                                'components.layout.profile_edit_modal.labels.phone'
                              )}
                              rules={[
                                {
                                  validator: (_, value) => {
                                    if (!value) return Promise.resolve();
                                    if (value.valid()) return Promise.resolve();
                                    if (
                                      !value.valid() &&
                                      value.phoneNumber === null
                                    )
                                      return Promise.resolve();
                                    return Promise.reject(
                                      new Error(
                                        t(
                                          'components.layout.profile_edit_modal.validations.phone_invalid'
                                        )
                                      )
                                    );
                                  },
                                },
                              ]}
                            >
                              <PhoneInput
                                enableSearch
                                searchNotFound={t(
                                  'components.layout.profile_edit_modal.messages.country_not_found'
                                )}
                                searchPlaceholder={t(
                                  'components.layout.profile_edit_modal.placeholders.search_country'
                                )}
                                allowClear
                                placeholder={t(
                                  'components.layout.profile_edit_modal.placeholders.enter_phone'
                                )}
                                disabled={sectionBusy}
                                onChange={(v) => {
                                  if (
                                    !v.countryCode &&
                                    !v.phoneNumber &&
                                    !v.areaCode
                                  ) {
                                    personalForm.setFieldValue('phone', {
                                      ...personalForm.getFieldValue('phone'),
                                      countryCode: user?.company?.country
                                        ?.dialCode
                                        ? parseInt(
                                            String(
                                              user?.company?.country?.dialCode
                                            )
                                          )
                                        : parseInt('1'),
                                    });
                                  }
                                }}
                              />
                            </Form.Item>
                          </Col>
                        </Row>
                        <Row gutter={24}>
                          <Col xs={24} md={12}>
                            <Form.Item
                              name="company"
                              label={t(
                                'components.layout.profile_edit_modal.labels.company'
                              )}
                            >
                              <Input disabled addonBefore={<BankOutlined />} />
                            </Form.Item>
                          </Col>
                          <Col xs={24} md={12}>
                            <Form.Item
                              name="country"
                              label={t(
                                'components.layout.profile_edit_modal.labels.country'
                              )}
                            >
                              <Input disabled addonBefore={<FlagOutlined />} />
                            </Form.Item>
                          </Col>
                        </Row>
                        <Row gutter={24}>
                          <Col xs={24}>
                            <Form.Item
                              name="roles"
                              label={t(
                                'components.layout.profile_edit_modal.labels.roles'
                              )}
                            >
                              <Space wrap size={8}>
                                {(personalForm.getFieldValue('roles') || [])
                                  .length > 0 ? (
                                  (
                                    personalForm.getFieldValue('roles') || []
                                  ).map((r: string, idx: number) => (
                                    <Tag
                                      key={`${r}-${idx}`}
                                      color={deriveAnalogous(
                                        customConfig.theme.token
                                          ?.colorPrimary || '#1677ff',
                                        18,
                                        8
                                      )}
                                      style={{
                                        fontSize: 13,
                                        padding: '4px 10px',
                                        borderRadius: 6,
                                      }}
                                    >
                                      {' '}
                                      {r}{' '}
                                    </Tag>
                                  ))
                                ) : (
                                  <Tag
                                    style={{
                                      fontSize: 13,
                                      padding: '4px 10px',
                                      borderRadius: 6,
                                    }}
                                  >
                                    {t(
                                      'components.layout.profile_edit_modal.messages.none_assigned'
                                    )}
                                  </Tag>
                                )}
                              </Space>
                            </Form.Item>
                          </Col>
                        </Row>
                      </Form>
                    </Space>
                  ),
                },
                {
                  key: 'password',
                  label: (
                    <Space align="center" size={6}>
                      <LockOutlined />
                      <span>
                        {t(
                          'components.layout.profile_edit_modal.tabs.change_password'
                        )}
                      </span>
                    </Space>
                  ),
                  children: (
                    <Space
                      direction="vertical"
                      style={{ width: '100%' }}
                      size={isMobile ? 12 : 16}
                    >
                      <Alert
                        message={t(
                          'components.layout.profile_edit_modal.password_requirements.title'
                        )}
                        description={(() => {
                          const lengthOk = (newPwdPreview || '').length >= 8;
                          const numberOk = /\d/.test(newPwdPreview || '');
                          const uppercaseOk = /[A-Z]/.test(newPwdPreview || '');
                          const specialOk = /[^\w\s]/.test(newPwdPreview || '');
                          const okColor =
                            customConfig.theme.token?.colorSuccess || '#52c41a';
                          const noColor =
                            customConfig.theme.token?.colorTextSecondary ||
                            '#999';
                          const Line = ({
                            ok,
                            text,
                          }: {
                            ok: boolean;
                            text: string;
                          }) => (
                            <div
                              style={{
                                display: 'flex',
                                alignItems: 'center',
                                gap: 8,
                              }}
                            >
                              {ok ? (
                                <CheckCircleOutlined
                                  style={{ color: okColor }}
                                />
                              ) : (
                                <CloseCircleOutlined
                                  style={{ color: noColor }}
                                />
                              )}
                              <span>{text}</span>
                            </div>
                          );
                          return (
                            <div style={{ display: 'grid', gap: 6 }}>
                              <Line
                                ok={lengthOk}
                                text={t(
                                  'components.layout.profile_edit_modal.password_requirements.min_length'
                                )}
                              />
                              <Line
                                ok={numberOk}
                                text={t(
                                  'components.layout.profile_edit_modal.password_requirements.include_number'
                                )}
                              />
                              <Line
                                ok={uppercaseOk}
                                text={t(
                                  'components.layout.profile_edit_modal.password_requirements.include_uppercase'
                                )}
                              />
                              <Line
                                ok={specialOk}
                                text={t(
                                  'components.layout.profile_edit_modal.password_requirements.include_special'
                                )}
                              />
                            </div>
                          );
                        })()}
                        type="info"
                        showIcon
                      />
                      <Divider style={{ margin: '8px 0' }} />
                      <Form form={passwordForm} layout="vertical">
                        <Row gutter={24}>
                          <Col xs={24} md={12}>
                            <Form.Item
                              name="currentPassword"
                              label={t(
                                'components.layout.profile_edit_modal.labels.current_password'
                              )}
                              rules={[
                                {
                                  required: true,
                                  message: t(
                                    'components.layout.profile_edit_modal.validations.current_password_required'
                                  ),
                                },
                              ]}
                            >
                              <Input.Password
                                disabled={saving}
                                placeholder={t(
                                  'components.layout.profile_edit_modal.placeholders.enter_password'
                                )}
                              />
                            </Form.Item>
                          </Col>
                        </Row>
                        <Row gutter={24}>
                          <Col xs={24} md={12}>
                            <Form.Item
                              name="newPassword"
                              label={t(
                                'components.layout.profile_edit_modal.labels.new_password'
                              )}
                              rules={[
                                {
                                  required: true,
                                  message: t(
                                    'components.layout.profile_edit_modal.validations.new_password_required'
                                  ),
                                },
                                {
                                  validator: (_, value) => {
                                    if (!value) return Promise.resolve();
                                    return passwordRulesRegex.test(value)
                                      ? Promise.resolve()
                                      : Promise.reject(
                                          new Error(
                                            t(
                                              'components.layout.profile_edit_modal.validations.new_password_invalid'
                                            )
                                          )
                                        );
                                  },
                                },
                              ]}
                            >
                              <Input.Password
                                disabled={saving}
                                placeholder={t(
                                  'components.layout.profile_edit_modal.placeholders.enter_new_password'
                                )}
                                onChange={(e) =>
                                  setNewPwdPreview(e.target.value)
                                }
                              />
                            </Form.Item>
                          </Col>
                          <Col xs={24} md={12}>
                            <Form.Item
                              name="confirmNewPassword"
                              label={t(
                                'components.layout.profile_edit_modal.labels.confirm_new_password'
                              )}
                              dependencies={['newPassword']}
                              rules={[
                                {
                                  required: true,
                                  message: t(
                                    'components.layout.profile_edit_modal.validations.confirm_password_required'
                                  ),
                                },
                                ({ getFieldValue }) => ({
                                  validator(_, value) {
                                    if (
                                      !value ||
                                      getFieldValue('newPassword') === value
                                    )
                                      return Promise.resolve();
                                    return Promise.reject(
                                      new Error(
                                        t(
                                          'components.layout.profile_edit_modal.validations.passwords_mismatch'
                                        )
                                      )
                                    );
                                  },
                                }),
                              ]}
                            >
                              <Input.Password
                                disabled={saving}
                                placeholder={t(
                                  'components.layout.profile_edit_modal.placeholders.confirm_new_password'
                                )}
                              />
                            </Form.Item>
                          </Col>
                        </Row>
                      </Form>
                    </Space>
                  ),
                },
                {
                  key: 'notifications',
                  label: (
                    <Space align="center" size={6}>
                      <BellOutlined />
                      <span>
                        {t(
                          'components.layout.profile_edit_modal.tabs.notifications'
                        )}
                      </span>
                    </Space>
                  ),
                  disabled: true,
                  children: <div />,
                },
              ]}
            />
          </Col>
        </Row>
      )}
    </Modal>
  );
}

export default ProfileEditModal;
